import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Navigate } from "@tanstack/react-router"
import { FiDownload, FiRefreshCw } from "react-icons/fi"

import { ClientDataExportApi } from "../../../api/clientDataExport"
import {
  type LicenseLimitDTO,
  type LicenseLimitUsageDTO,
  LicenseService,
} from "../../../api/license"
import type { UserPublic } from "../../../client"
import { WmsPageHeader } from "../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../components/WMS/shared/WmsTableListCard"
import { downloadBlob } from "../../../utils/downloadBlob"
import { isSuperAdmin } from "../../../utils/roles"

const limitLabels: Record<string, string> = {
  exports_per_day: "Eksporty na dzień",
  documents_per_day: "Dokumenty na dzień",
  max_active_users: "Aktywni użytkownicy",
  max_warehouses: "Magazyny",
  max_product_label_templates: "Szablony etykiet produktów",
}

const statusLabelsPl: Record<string, string> = {
  active: "Aktywna",
  expired_grace: "Wygasła — okres przejściowy",
  expired_limited: "Wygasła — ograniczony dostęp",
  expired_critical: "Wygasła — tryb krytyczny",
  suspended: "Zawieszona",
  unlicensed: "Brak licencji",
  revoked: "Cofnięta",
}

const featureLabelsPl: Record<string, string> = {
  advanced_analytics: "Zaawansowana analityka",
  advanced_reports: "Zaawansowane raporty",
  inventory_count: "Inwentaryzacja",
  technology_management: "Zarządzanie technologią",
  production_orders: "Zlecenia produkcyjne",
  auto_replenishment: "Automatyczne uzupełnianie zapasów",
  operational_analytics: "Analizy operacyjne",
  product_labels: "Etykiety produktów",
  ksef_integration: "Integracja z KSeF",
}

const moduleReasonLabelsPl: Record<string, string> = {
  included: "Dostępny w planie",
  plan_excluded: "Niedostępny w tym planie",
  license_override: "Wyłączony w ustawieniach licencji",
  dependency_disabled: "Wymagany moduł jest wyłączony",
  license_status: "Niedostępny z powodu stanu licencji",
  invalid_contract: "Nieprawidłowe dane licencji",
}

const enforcementModeLabelsPl: Record<string, string> = {
  enforce: "Blokowanie operacji",
  audit: "Rejestrowanie naruszeń",
}

const syncHealthLabelsPl: Record<string, string> = {
  synced: "Aktualna",
  stale: "Wymaga odświeżenia",
  error: "Błąd synchronizacji",
  unknown: "Brak informacji",
}

function formatDate(value?: string, timeZone?: string) {
  if (!value) return "—"
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return "—"
  }
  return parsed.toLocaleString("pl-PL", { timeZone })
}

export const Route = createFileRoute("/_layout/admin/license-status")({
  component: LicenseStatusPage,
})

function LicenseStatusPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const statBg = useColorModeValue("white", "gray.800")
  const statBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const scrollThumbBg = useColorModeValue("gray.300", "gray.600")
  const statusColorScheme: Record<
    string,
    "green" | "yellow" | "orange" | "red" | "gray"
  > = {
    active: "green",
    expired_grace: "yellow",
    expired_limited: "orange",
    expired_critical: "red",
    suspended: "red",
    unlicensed: "red",
    revoked: "red",
  }

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["license", "status"],
    queryFn: LicenseService.getStatus,
    enabled: isSuperAdmin(currentUser),
  })

  const triggerRefreshMutation = useMutation({
    mutationFn: LicenseService.triggerManualRefresh,
    onSuccess: (resp) => {
      if (resp.refreshTriggered) {
        toast({
          title: "Licencja została odświeżona",
          description: "Zaktualizowano stan licencji i dostęp do modułów WMS.",
          status: "success",
          duration: 2500,
        })
      } else {
        toast({
          title: "Aktualizacja nie została przeprowadzona",
          description:
            resp.error ||
            "Próba została przyjęta, ale odświeżanie nie zakończyło się pomyślnie.",
          status: "warning",
          duration: 5000,
        })
      }
      queryClient.invalidateQueries({ queryKey: ["license", "status"] })
      queryClient.invalidateQueries({ queryKey: ["license", "summary"] })
    },
    onError: (err: Error) => {
      toast({
        title: "Błąd ręcznej aktualizacji",
        description: err.message || "Nie udało się uruchomić odświeżania.",
        status: "error",
        duration: 5000,
      })
    },
  })
  const clientDataExportMutation = useMutation({
    mutationFn: () =>
      ClientDataExportApi.downloadArchive({ includeFiles: true }),
    onSuccess: ({ blob, fileName }) => {
      downloadBlob(blob, fileName)
      toast({
        title: "Archiwum danych zostało przygotowane",
        description:
          "Pobrano archiwum danych klienta. Pobranie nie zmienia danych w systemie.",
        status: "success",
        duration: 3500,
      })
    },
    onError: (err: Error) => {
      toast({
        title: "Nie udało się pobrać danych klienta",
        description:
          err.message ||
          "Spróbuj ponownie lub skontaktuj się z administratorem systemu.",
        status: "error",
        duration: 5000,
      })
    },
  })

  if (currentUser && !isSuperAdmin(currentUser)) {
    return <Navigate to="/admin" search={{ page: 1, pageSize: 10 }} />
  }

  if (isPending) {
    return (
      <WmsPageShell>
        <WmsPageHeader
          title="Status licencji"
          description="Bieżący stan licencji i dostępnych modułów WMS."
          gradient="linear(to-r, teal.600, green.600)"
          badge="Ładowanie"
        />
        <WmsTableListCard
          isLoading
          error={undefined}
          errorMessage="Nie udało się uzyskać statusu licencji"
          minEmptyHeight={{ base: "40vh", lg: 0 }}
        />
      </WmsPageShell>
    )
  }

  if (isError) {
    return (
      <WmsPageShell>
        <WmsPageHeader
          title="Status licencji"
          description="Bieżący stan licencji i dostępnych modułów WMS."
          gradient="linear(to-r, teal.600, green.600)"
          badge="Błąd"
        />
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {(error as Error)?.message ||
            "Nie udało się uzyskać statusu licencji"}
        </Alert>
      </WmsPageShell>
    )
  }

  if (!data) {
    return null
  }

  const checkedAt = formatDate(data.checkedAt)
  const statusLabel = statusLabelsPl[data.status] ?? "Nieznany stan"
  const syncHealth = data.lastSyncAttemptFailed ? "error" : data.syncHealth
  const statusScheme = statusColorScheme[data.status] ?? "gray"
  const usageByLimitKey = new Map<string, LicenseLimitUsageDTO>(
    (data.limitUsage ?? []).map((item) => [item.key, item]),
  )

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Status licencji"
        description="Bieżący stan licencji i dostępnych modułów WMS."
        gradient="linear(to-r, teal.600, green.600)"
        actions={
          <>
            <Button
              size="sm"
              bg="white"
              color="teal.700"
              borderColor="white"
              variant="outline"
              leftIcon={<FiDownload />}
              isLoading={clientDataExportMutation.isPending}
              _hover={{ bg: "teal.50", borderColor: "white" }}
              _active={{ bg: "teal.100" }}
              onClick={() => clientDataExportMutation.mutate()}
            >
              Pobierz dane klienta
            </Button>
            <Button
              size="sm"
              colorScheme="teal"
              variant="solid"
              leftIcon={<FiRefreshCw />}
              isLoading={triggerRefreshMutation.isPending}
              onClick={() => triggerRefreshMutation.mutate()}
            >
              Odśwież licencję
            </Button>
          </>
        }
        badge={
          <Badge
            colorScheme={statusScheme}
            variant="solid"
            borderRadius="full"
            px={4}
            py={1.5}
            textTransform="uppercase"
          >
            {statusLabel}
          </Badge>
        }
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <Box
          role="region"
          aria-label="Szczegóły licencji"
          tabIndex={0}
          flex="1"
          minH={0}
          maxH={{ base: "50dvh", lg: "none" }}
          overflowY="auto"
          overflowX="hidden"
          sx={{
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: scrollThumbBg,
              borderRadius: "full",
            },
          }}
        >
          <SimpleGrid columns={{ base: 1, md: 2, xl: 7 }} spacing={4}>
            <Stat
              bg={statBg}
              borderWidth="1px"
              borderColor={statBorder}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <StatLabel>Plan</StatLabel>
              <StatNumber fontSize="lg" overflowWrap="anywhere">
                {data.plan || "—"}
              </StatNumber>
            </Stat>
            <Stat
              bg={statBg}
              borderWidth="1px"
              borderColor={statBorder}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <StatLabel>Klient</StatLabel>
              <StatNumber fontSize="lg" overflowWrap="anywhere">
                {data.customerCode || "—"}
              </StatNumber>
            </Stat>
            <Stat
              bg={statBg}
              borderWidth="1px"
              borderColor={statBorder}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <StatLabel>Tryb kontroli</StatLabel>
              <StatNumber fontSize="lg" overflowWrap="anywhere">
                {enforcementModeLabelsPl[data.capabilityEnforcementMode] ??
                  "Nieznany tryb"}
              </StatNumber>
            </Stat>
            <Stat
              bg={statBg}
              borderWidth="1px"
              borderColor={statBorder}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <StatLabel>Kontrola licencji</StatLabel>
              <StatNumber fontSize="lg" overflowWrap="anywhere">
                {data.enforcementEnabled ? "Włączona" : "Wyłączona"}
              </StatNumber>
            </Stat>
            <Stat
              bg={statBg}
              borderWidth="1px"
              borderColor={statBorder}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <StatLabel>Ostatnia kontrola</StatLabel>
              <StatNumber fontSize="lg" overflowWrap="anywhere">
                {checkedAt}
              </StatNumber>
            </Stat>
            <Stat
              bg={statBg}
              borderWidth="1px"
              borderColor={statBorder}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <StatLabel>Rewizja licencji</StatLabel>
              <StatNumber fontSize="lg" overflowWrap="anywhere">
                {data.licenseRevision}
              </StatNumber>
              <Text fontSize="xs" color={mutedText}>
                Wersja kontraktu: {data.contractVersion}
              </Text>
            </Stat>
            <Stat
              bg={statBg}
              borderWidth="1px"
              borderColor={statBorder}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <StatLabel>Synchronizacja</StatLabel>
              <StatNumber fontSize="lg" overflowWrap="anywhere">
                {syncHealthLabelsPl[syncHealth] ?? "Nieznany stan"}
              </StatNumber>
              <Text fontSize="xs" color={mutedText}>
                {data.lastSyncAttemptAt
                  ? `Ostatnia próba: ${formatDate(data.lastSyncAttemptAt)}`
                  : "Nie odnotowano próby synchronizacji"}
              </Text>
              <Text fontSize="xs" color={mutedText}>
                {data.lastSuccessfulSyncAt
                  ? `Ostatnia udana synchronizacja: ${formatDate(data.lastSuccessfulSyncAt)}`
                  : "Brak udanej synchronizacji"}
              </Text>
              {data.syncSource === "remote_refresh" ? (
                <Text fontSize="xs" color={mutedText}>
                  Źródło: serwer licencji
                </Text>
              ) : null}
              {data.syncFailureCount > 0 ? (
                <Text fontSize="xs" color={mutedText}>
                  Nieudane próby: {data.syncFailureCount}
                </Text>
              ) : null}
            </Stat>
          </SimpleGrid>

          {data.status === "revoked" ||
          data.status === "unlicensed" ||
          data.status === "suspended" ||
          data.status === "expired_critical" ? (
            <Alert status="error" borderRadius="xl" mt={4}>
              <AlertIcon />
              Stan licencji: {statusLabel}. Część operacji jest niedostępna do
              czasu odnowienia lub ponownego aktywowania licencji.
            </Alert>
          ) : null}

          {data.daysSinceExpiry > 0 ? (
            <Alert status="warning" borderRadius="xl" mt={4}>
              <AlertIcon />
              Licencja jest po terminie od {data.daysSinceExpiry} dni.
            </Alert>
          ) : null}

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4} mt={2}>
            <WmsTableListCard
              error={undefined}
              errorMessage=""
              minEmptyHeight="auto"
            >
              <Box p={3}>
                <Heading size="sm" mb={2}>
                  Dostęp do modułów
                </Heading>
                {data.modules.length === 0 ? (
                  <Text color={mutedText}>
                    Brak informacji o dostępnych modułach.
                  </Text>
                ) : (
                  <TableContainer>
                    <Table size="sm" whiteSpace="normal">
                      <Tbody>
                        {data.modules.map((module) => (
                          <Tr key={module.key}>
                            <Td fontWeight="semibold">
                              {featureLabelsPl[module.key] ?? "Nieznany moduł"}
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={module.enabled ? "green" : "red"}
                                variant="subtle"
                              >
                                {module.enabled ? "włączony" : "wyłączony"}
                              </Badge>
                              <Text mt={1} fontSize="xs" color={mutedText}>
                                {moduleReasonLabelsPl[module.reason] ??
                                  "Brak informacji o przyczynie"}
                                {module.blockedBy
                                  ? `: ${featureLabelsPl[module.blockedBy] ?? "Nieznany moduł"}`
                                  : ""}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </WmsTableListCard>

            <WmsTableListCard
              error={undefined}
              errorMessage=""
              minEmptyHeight="auto"
            >
              <Box p={3}>
                <Heading size="sm" mb={2}>
                  Identyfikacja licencji
                </Heading>
                <TableContainer>
                  <Table size="sm" whiteSpace="normal">
                    <Tbody>
                      <Tr>
                        <Td fontWeight="semibold">Identyfikator licencji</Td>
                        <Td overflowWrap="anywhere">{data.licenseId || "—"}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold">Identyfikator klienta</Td>
                        <Td overflowWrap="anywhere">
                          {data.customerId || "—"}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold">Kod klienta</Td>
                        <Td overflowWrap="anywhere">
                          {data.customerCode || "—"}
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </WmsTableListCard>

            <WmsTableListCard
              error={undefined}
              errorMessage=""
              minEmptyHeight="auto"
            >
              <Box p={3}>
                <Heading size="sm" mb={2}>
                  Limity
                </Heading>
                {data.limits.length === 0 ? (
                  <Text color={mutedText}>Limity nie zostały ustawione.</Text>
                ) : (
                  <TableContainer>
                    <Table size="sm" whiteSpace="normal">
                      <Tbody>
                        {data.limits.map((limit: LicenseLimitDTO) => (
                          <Tr key={limit.key}>
                            <Td fontWeight="semibold">
                              {limitLabels[limit.key] ?? "Inny limit"}
                            </Td>
                            <Td>
                              <Text>{limit.value}</Text>
                              {usageByLimitKey.get(limit.key) ? (
                                <Text fontSize="xs" color={mutedText}>
                                  {`Wykorzystano: ${usageByLimitKey.get(limit.key)?.current ?? 0}/${usageByLimitKey.get(limit.key)?.max ?? limit.value} · Pozostało: ${usageByLimitKey.get(limit.key)?.remaining ?? 0}`}
                                </Text>
                              ) : null}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </WmsTableListCard>

            <WmsTableListCard
              error={undefined}
              errorMessage=""
              minEmptyHeight="auto"
            >
              <Box p={3}>
                <Heading size="sm" mb={2}>
                  Harmonogram zmian statusu (UTC)
                </Heading>
                {data.transitionTimeline.length === 0 ? (
                  <Text color={mutedText}>
                    Brak zaplanowanych zmian statusu licencji.
                  </Text>
                ) : (
                  data.transitionTimeline.map((item) => (
                    <Flex
                      key={`${item.toStatus}-${item.atUtc}`}
                      justify="space-between"
                      align={{ base: "flex-start", md: "center" }}
                      direction={{ base: "column", md: "row" }}
                      gap={2}
                      borderWidth="1px"
                      borderColor={panelBorder}
                      borderRadius="md"
                      px={3}
                      py={2}
                      mb={2}
                    >
                      <Box flex="1" minW={0}>
                        <Text fontWeight="semibold">
                          {statusLabelsPl[item.toStatus] ?? "Nieznany stan"}
                        </Text>
                      </Box>
                      <Text color={mutedText} whiteSpace="nowrap">
                        {formatDate(item.atUtc, "UTC")}
                      </Text>
                    </Flex>
                  ))
                )}
              </Box>
            </WmsTableListCard>
          </SimpleGrid>
        </Box>
      </WmsPagePanel>
    </WmsPageShell>
  )
}
